import * as React from 'react';
import { CreateButton, Panel, Spinner, Switch } from '@ixicare/ui';
import { Link, useLocation } from 'react-router-dom';
import { Building, SquarePen } from 'lucide-react';
import { observer } from 'mobx-react';
import { t } from '~i18n';
import { useStores } from '~/store/utils';
import OrganizationTree from '../components/OrganizationTree';
import {
  findOrganizationPath,
  flattenOrganizations,
  organizationAnchor,
  rememberOrganizationAnchor,
  useOrganizations,
} from '../hooks/useOrganizations';
import styles from './SiteList.module.css';

const SiteSettingsPage = observer(() => {
  const { sites, permissions, auth } = useStores();
  const [editModeRequested, setEditModeRequested] = React.useState(false);
  const canManage = permissions.has('can_manage_organizations');
  const editMode = canManage && editModeRequested;

  const organizationsQuery = useOrganizations();
  const { hash } = useLocation();
  const anchorPrefix = `#${organizationAnchor('')}`;
  const focusOrgId = hash.startsWith(anchorPrefix) ? decodeURIComponent(hash.slice(anchorPrefix.length)) : null;

  React.useEffect(() => {
    sites.loadSites();
  }, [sites]);

  const orgs = organizationsQuery.data ?? [];

  const siteUuidToId = React.useMemo(() => {
    const map = new Map<string, number>();
    sites.sites.forEach((s) => map.set(s.uuid, s.id));
    return map;
  }, [sites.sites]);

  const assignedSiteUuids = React.useMemo(
    () => new Set(flattenOrganizations(orgs).flatMap(({ node }) => node.sites.map((s) => s.uuid))),
    [orgs]
  );

  const unassignedSites = React.useMemo(
    () => sites.sites.filter((s) => !assignedSiteUuids.has(s.uuid)),
    [sites.sites, assignedSiteUuids]
  );

  const loading = sites.loading || organizationsQuery.isLoading;
  const currentSite = sites.selectedSite;
  const currentSiteOrg = currentSite
    ? findOrganizationPath(orgs, (o) => o.sites.some((s) => s.uuid === currentSite.uuid)).at(-1)
    : undefined;

  return (
    <>
      <Panel>
        <Panel.Title
          extraInfo={
            canManage ? (
              <span className={styles.EditToggle}>
                {t('siteSettings.organization.editToggle')}
                <Switch label="" checked={editMode} onChange={setEditModeRequested} />
              </span>
            ) : undefined
          }
        >
          {t('siteSettings.allBuildings')}
        </Panel.Title>
        {currentSite && (
          <Link
            to={`/sites/${currentSite.uuid}`}
            className={styles.CurrentSite}
            onClick={currentSiteOrg ? () => rememberOrganizationAnchor(currentSiteOrg.id) : undefined}
          >
            <Building className={styles.CurrentSiteIcon} size={18} strokeWidth={1.85} aria-hidden="true" />
            <span className={styles.CurrentSiteLabels}>
              <span className={styles.CurrentSiteEyebrow}>{t('siteSettings.editCurrentSite')}</span>
              <span className={styles.CurrentSiteName}>{currentSite.name}</span>
            </span>
            <SquarePen className={styles.CurrentSiteEdit} size={16} aria-hidden="true" />
          </Link>
        )}
        {loading ? (
          <Spinner />
        ) : (
          <OrganizationTree
            organizations={orgs}
            unassignedSites={unassignedSites}
            editMode={editMode}
            scopeRootId={auth.user?.permissionOrganizationId ?? null}
            siteUuidToId={siteUuidToId}
            focusOrgId={focusOrgId}
            onSitesChanged={() => sites.reloadSites()}
          />
        )}
        <Link to="/sites/create">
          <CreateButton atBottom />
        </Link>
      </Panel>
    </>
  );
});

export default SiteSettingsPage;
